import React, { useState } from 'react';
import {
  Globe,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ArrowRight,
  RotateCcw
} from 'lucide-react';

import ManualAffiliateDetails from '../paidTraffic/ManualAffiliateDetails';

const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

const PIPELINE = ['Domain Rank', 'Organic Keywords', 'Paid Keywords', 'Top Geos', 'Merge into affiliate_clean'];

const fmtNumber = (n) =>
  n === null || n === undefined || n === '' ? '—' : Number(n).toLocaleString();

const splitList = (value) =>
  !value ? [] : String(value).split(',').map((v) => v.trim()).filter(Boolean);

const AddAffiliate = () => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  // Set when SEMrush returned nothing and the operator must supply a country
  // and vertical before the row can be created.
  const [manualDomain, setManualDomain] = useState(null);

  const submit = async (extra = {}) => {
    const trimmed = url.trim();
    if (!trimmed) {
      setError('Please enter an affiliate URL');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${basePath}/api/paid-traffic/add-affiliate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: trimmed, ...extra })
      });

      if (res.status === 403) {
        setError('Access denied — this page is available to the admin account only.');
        return;
      }

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || `Server error: ${res.status}`);
      }

      if (data.needsManualDetails) {
        setManualDomain(data.domain);
        setResult(null);
        return;
      }

      setManualDomain(null);
      setResult(data);
      if (!data.success) setError(data.message || 'The affiliate could not be added.');
    } catch (err) {
      setError(err.message || 'Failed to add affiliate');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setResult(null);
    setManualDomain(null);
    submit();
  };

  const handleReset = () => {
    setUrl('');
    setResult(null);
    setError('');
    setManualDomain(null);
  };

  const affiliate = result?.affiliate;

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100">Add Affiliate</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
          Enter a URL and it runs the full SEMrush pipeline — domain rank, top pages, top keywords and top geos —
          then merges the result into{' '}
          <code className="rounded bg-gray-100 px-1 py-0.5 text-xs dark:bg-slate-800">affiliate_clean</code>.
          If SEMrush has no data for the domain, it is still added with minimal data from the region API.
        </p>
      </div>

      {/* Pipeline hint */}
      <div className="mb-5 flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
        {PIPELINE.map((step, i) => (
          <React.Fragment key={step}>
            <span className="text-xs font-medium text-gray-600 dark:text-slate-400">{step}</span>
            {i < PIPELINE.length - 1 && <ArrowRight className="h-3 w-3 text-gray-300 dark:text-slate-600" />}
          </React.Fragment>
        ))}
      </div>

      <form
        onSubmit={handleSubmit}
        className="rounded-xl border border-gray-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900"
      >
        <label htmlFor="affiliate-url" className="mb-2 block text-sm font-medium text-gray-700 dark:text-slate-300">
          Affiliate URL <span className="text-red-500">*</span>
        </label>
        <div className="flex flex-wrap gap-3">
          <div className="relative min-w-[280px] flex-1">
            <Globe className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              id="affiliate-url"
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={loading}
              placeholder="example.com"
              className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 outline-none focus:border-blue-500 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
            {loading ? 'Processing…' : 'Add Affiliate'}
          </button>
          {!loading && (result || error) && (
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </button>
          )}
        </div>
        <p className="mt-2 text-xs text-gray-400 dark:text-slate-500">
          One domain at a time. The scheme and <code>www.</code> are stripped automatically.
        </p>
      </form>

      {loading && (
        <div className="mt-4 flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-800/50 dark:bg-blue-900/20 dark:text-blue-300">
          <Loader2 className="h-4 w-4 animate-spin" />
          Running the SEMrush pipeline — this can take up to a minute.
        </div>
      )}

      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800/50 dark:bg-red-900/20 dark:text-red-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {manualDomain && !loading && (
        <div className="mt-4">
          <ManualAffiliateDetails
            domain={manualDomain}
            busy={loading}
            onConfirm={({ country, vertical }) => submit({ country, vertical })}
            onCancel={handleReset}
          />
        </div>
      )}

      {result && (
        <div className="mt-5 space-y-4">
          <div
            className={`flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${
              result.success
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800/50 dark:bg-emerald-900/20 dark:text-emerald-300'
                : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800/50 dark:bg-amber-900/20 dark:text-amber-300'
            }`}
          >
            {result.success ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
            ) : (
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            )}
            <div>
              <div className="font-medium">{result.message}</div>
              {result.usedFallback && (
                <div className="mt-0.5 text-xs opacity-80">
                  SEMrush had no data for this domain — minimal data was used instead.
                </div>
              )}
            </div>
          </div>

          {/* Per-step results */}
          {Array.isArray(result.steps) && result.steps.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900">
              <div className="border-b border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 dark:border-slate-700 dark:text-slate-300">
                Pipeline steps
              </div>
              <ul className="divide-y divide-gray-100 dark:divide-slate-800">
                {result.steps.map((step, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                    <span className="flex items-center gap-2 text-gray-700 dark:text-slate-300">
                      {step.success ? (
                        <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-emerald-500" />
                      ) : (
                        <XCircle className="h-4 w-4 flex-shrink-0 text-red-500" />
                      )}
                      {step.step}
                    </span>
                    <span className="text-right text-xs text-gray-500 dark:text-slate-400">{step.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* The row as it now stands in affiliate_clean */}
          {affiliate && (
            <div className="rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900">
              <div className="border-b border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 dark:border-slate-700 dark:text-slate-300">
                Row in affiliate_clean
              </div>
              <div className="grid grid-cols-1 gap-4 px-4 py-4 sm:grid-cols-2">
                <Field label="Affiliate URL" value={affiliate.affiliate_url} mono />
                <Field label="Vertical" value={affiliate.vertical || '—'} />
                <Field label="Organic Traffic" value={fmtNumber(affiliate.organic_traffic)} />
                <Field label="Paid Traffic" value={fmtNumber(affiliate.adwords_traffic)} />
                <ListField label="Top Pages" value={affiliate.top_page} />
                <ListField label="Top Geos" value={affiliate.top_geos} />
                <ListField label="Top 5 Keywords" value={affiliate.keywords} />
                <ListField label="Top Paid Keywords" value={affiliate.paid_keyword} />
              </div>
              <div className="border-t border-gray-200 px-4 py-3 dark:border-slate-700">
                <a
                  href={`/paid-traffic?search=${encodeURIComponent(affiliate.affiliate_url)}`}
                  className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                >
                  Open it in Paid Traffic to set paid traffic →
                </a>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, mono = false }) => (
  <div>
    <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-slate-500">{label}</div>
    <div className={`text-sm text-gray-900 dark:text-slate-100 ${mono ? 'font-mono' : ''}`}>{value}</div>
  </div>
);

const ListField = ({ label, value }) => {
  const items = splitList(value);
  return (
    <div>
      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-slate-500">{label}</div>
      {items.length === 0 ? (
        <div className="text-sm text-gray-400 dark:text-slate-600">—</div>
      ) : (
        <div className="flex flex-wrap gap-1">
          {items.map((item, i) => (
            <span
              key={i}
              title={item}
              className="inline-block max-w-[240px] truncate rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-700 dark:bg-slate-800 dark:text-slate-300"
            >
              {item}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddAffiliate;
